const API_URL = import.meta.env.VITE_API_URL || 'https://api.allgrassroots.invalid';

interface ApiOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
}

async function apiRequest<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const token = localStorage.getItem('authToken');

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const config: RequestInit = {
    method: options.method || 'GET',
    headers,
  };

  if (options.body) {
    config.body = JSON.stringify(options.body);
  }

  const response = await fetch(`${API_URL}${endpoint}`, config);

  if (response.status === 401) {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    window.location.href = '/';
    throw new Error('Session expired. Please log in again.');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || error.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

// Authentication
export interface LoginResponse {
  token: string;
  clubId: string;
  email: string;
  name?: string;
  clubName: string;
  badgeUrl?: string;
  plan: string;
  role: string;
  forcePasswordChange?: boolean;
  demoMode?: boolean;
  /** Per-club feature flags. Snapshot at login. */
  features?: Record<string, boolean>;
}

export async function loginRequest(email: string, password: string): Promise<LoginResponse> {
  return apiRequest<LoginResponse>('/api/v1/auth/login', {
    method: 'POST',
    body: { email, password },
  });
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  return apiRequest<void>('/api/v1/admin/change-password', {
    method: 'POST',
    body: { currentPassword, newPassword },
  });
}

// Content Management
export interface ContentSection {
  id: string;
  section: string;
  content: Record<string, unknown>;
  version: 'draft' | 'published';
  updatedAt: string;
  updatedBy: string;
}

export async function getAllContent(version: 'draft' | 'published' = 'draft'): Promise<ContentSection[]> {
  const response = await apiRequest<{ content: any[] }>(`/api/v1/admin/content?version=${version}`);
  const content = Array.isArray(response?.content) ? response.content : [];
  return content.map(item => ({
    id: item.content_id || item.id,
    section: item.content_id || item.section,
    content: item.data || item.content || {},
    version: item.version || 'draft',
    updatedAt: item.updated_at || item.updatedAt || '',
    updatedBy: item.updated_by || item.updatedBy || '',
  }));
}

export async function getContent(section: string, version: 'draft' | 'published' = 'draft'): Promise<ContentSection> {
  return apiRequest<ContentSection>(`/api/v1/admin/content/${section}?version=${version}`);
}

export async function saveContent(section: string, content: Record<string, unknown>): Promise<ContentSection> {
  const res = await apiRequest<ContentSection>(`/api/v1/admin/content/${section}`, {
    method: 'PUT',
    body: { content, version: 'draft' },
  });
  // Notify the PreviewBar so it re-polls /preview/status without
  // waiting for its 20s interval. No-op for clubs without preview.
  window.dispatchEvent(new Event('preview-changed'));
  return res;
}

export async function publishContent(section: string): Promise<ContentSection> {
  const res = await apiRequest<ContentSection>(`/api/v1/admin/content/${section}/publish`, {
    method: 'POST',
  });
  window.dispatchEvent(new Event('preview-changed'));
  return res;
}

export async function deleteContent(section: string): Promise<void> {
  return apiRequest<void>(`/api/v1/admin/content/${section}`, {
    method: 'DELETE',
  });
}

// Product Management
/** Buyer-supplied text on a product, "SMITH 9" on the back of a shirt.
 *  `maxLength` is what the club set; the API clamps it to its own ceiling. */
export interface Personalisation {
  enabled: boolean;
  label: string;
  maxLength: number;
}

/** Per-product postage. null = collection only (today's behaviour). */
export interface Delivery {
  available: boolean;
  /** In pounds. What it costs to post THIS item on its own. */
  fee: number;
}

export interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  salePrice?: number;
  category: string;
  sizes: string[];
  /** null = made to order (no stock count, never sold out). */
  stock: number | null;
  /** null = not offered on this product. */
  personalisation: Personalisation | null;
  /** null = collection only. */
  delivery: Delivery | null;
  images: string[];
  createdAt: string;
  updatedAt: string;
  hiddenFromPublic?: boolean;
}

export interface ProductsResponse {
  products: Product[];
  plan: string;
  limit: number | null;
  hiddenCount: number;
}

export async function getProducts(): Promise<ProductsResponse> {
  const response = await apiRequest<{ products: any[]; plan?: string; limit?: number | null; hiddenCount?: number }>('/api/v1/admin/products');
  const products = Array.isArray(response?.products) ? response.products : [];
  return {
    products: products.map(product => ({
      id: product.product_id || product.id,
      name: product.name || '',
      description: product.description || '',
      price: product.price || 0,
      salePrice: product.sale_price || product.salePrice || 0,
      category: product.category || 'Merchandise',
      sizes: product.has_sizes ? Object.keys(product.stock_by_size || {}) : [],
      stock: product.has_sizes
        ? Object.values(product.stock_by_size || {}).reduce((sum: number, val: any) => sum + (val || 0), 0)
        : (product.stock === null || product.stock === undefined ? null : Number(product.stock)),
      personalisation: product.personalisation?.enabled ? product.personalisation : null,
      delivery: product.delivery?.available ? product.delivery : null,
      images: Array.isArray(product.images) ? product.images : [],
      createdAt: product.created_at || product.createdAt || '',
      updatedAt: product.updated_at || product.updatedAt || '',
      hiddenFromPublic: product.hiddenFromPublic === true,
    })),
    plan: response?.plan || 'free',
    limit: response?.limit ?? null,
    hiddenCount: response?.hiddenCount || 0,
  };
}

export async function getProduct(id: string): Promise<Product> {
  return apiRequest<Product>(`/api/v1/admin/products/${id}`);
}

export async function createProduct(product: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>): Promise<Product> {
  return apiRequest<Product>('/api/v1/admin/products', {
    method: 'POST',
    body: product,
  });
}

export async function updateProduct(id: string, product: Partial<Product>): Promise<Product> {
  return apiRequest<Product>(`/api/v1/admin/products/${id}`, {
    method: 'PUT',
    body: product,
  });
}

export async function deleteProduct(id: string): Promise<void> {
  return apiRequest<void>(`/api/v1/admin/products/${id}`, {
    method: 'DELETE',
  });
}

// Club Management
export interface Coach {
  id: string;
  name: string;
  image: string;
  coachTitle?: string;
}

/** Coaching titles shown on the public team page. Shared by the Coaches tab
 *  and the Teams-tab inline coach editor so the two can't drift. */
export const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
export const DAY_TIMES = ['Morning', 'Afternoon', 'Evening'];

export const COACH_TITLES = ['Manager', 'Assistant Manager', 'Head Coach', 'Lead Coach', 'Coach', 'Assistant Coach', 'Goalkeeping Coach'];

/**
 * Age groups, in the order teams should appear. Was a free-text box, which gave
 * us `U14`, `Under-14s`, `Under 11` and `UN 16, Adult ` all meaning much the
 * same thing, and `Open Age` on 65 teams, which no amount of parsing sorts.
 * Picking from a list makes the public ordering an index lookup instead of a
 * guess. A club's existing value is kept as an extra option if it isn't here.
 */
export const AGE_GROUPS = [
  'Under 6', 'Under 7', 'Under 8', 'Under 9', 'Under 10', 'Under 11', 'Under 12',
  'Under 13', 'Under 14', 'Under 15', 'Under 16', 'Under 17', 'Under 18',
  'Under 21', 'Under 23', 'Open Age', 'Veterans',
];

/** Per-team sponsor (Club/Pro), same shape as a club sponsor, no tiers. */
export interface TeamSponsor { id: string; name: string; image: string; url: string }

/** Who the team is for, drives the grouped sections on the public Teams page. */
export const TEAM_CATEGORIES = [
  { value: '', label: 'Not specified' },
  { value: 'boys', label: 'Boys' },
  { value: 'girls', label: 'Girls' },
  { value: 'men', label: 'Men' },
  { value: 'women', label: 'Women' },
  { value: 'mixed', label: 'Mixed' },
];

export interface Team {
  id: string;
  name: string;
  ageGroup: string;
  teamCategory?: string;
  hideSquad?: boolean;
  venueId?: string;
  league?: string;
  matchDay?: string;
  matchTime?: string;
  trainingDay?: string;
  trainingTime?: string;
  trainingStart?: string;
  trainingEnd?: string;
  sponsors?: TeamSponsor[];
  coach: string;
  coaches: Coach[];
  description: string;
  image: string;
  createdAt: string;
  updatedAt: string;
}

// Normalise any team shape the API returns (snake_case, teamId vs id,
// or wrapped in { team }) into a consistent Team with `.id`. Used by
// every team client fn so callers always get a usable `.id`, the
// TeamWizard relied on createTeam's `.id` and got undefined before this.
function normalizeTeam(raw: any): Team {
  const team = raw?.team ?? raw ?? {};
  return {
    id: team.team_id || team.teamId || team.id,
    name: team.name || '',
    ageGroup: team.age_group || team.ageGroup || '',
    // These four were missing until 2026-08-20, and their absence was
    // silently DESTRUCTIVE: the edit form read them back as empty, then the
    // save wrote those empties over the real values. Editing a team's name
    // wiped its category, its venue, its sponsors, and un-hid a squad page
    // a club had deliberately hidden. Reported by Whiteknights ("the Team For
    // flag resets every time I edit"); the other three were losing data
    // without anyone noticing.
    teamCategory: team.team_category || team.teamCategory || '',
    hideSquad: team.hide_squad ?? team.hideSquad ?? false,
    venueId: team.venue_id || team.venueId || '',
    league: team.league || '',
    matchDay: team.match_day || team.matchDay || '',
    matchTime: team.match_time || team.matchTime || '',
    trainingDay: team.training_day || team.trainingDay || '',
    trainingTime: team.training_time || team.trainingTime || '',
    trainingStart: team.training_start || team.trainingStart || '',
    trainingEnd: team.training_end || team.trainingEnd || '',
    sponsors: Array.isArray(team.sponsors) ? team.sponsors : [],
    coach: team.coach || '',
    coaches: Array.isArray(team.coaches) ? team.coaches : [],
    description: team.description || '',
    image: team.image || '',
    createdAt: team.created_at || team.createdAt || '',
    updatedAt: team.updated_at || team.updatedAt || '',
  };
}

export async function getTeams(): Promise<Team[]> {
  const response = await apiRequest<{ teams: any[] }>('/api/v1/admin/teams');
  const teams = Array.isArray(response?.teams) ? response.teams : [];
  return teams.map(normalizeTeam);
}

// Sponsors Management
export interface Sponsor {
  id: string;
  name: string;
  image: string;
  url?: string;
}

export interface SponsorTier {
  id: string;
  name: string;
  sponsors: Sponsor[];
}

/** How the public page lays sponsor logos out. 'auto' wraps them into rows,
 *  or scrolls them on one line once a tier reaches seven; 'paged' is a single
 *  row with arrows, each sponsor shown once. */
export type SponsorDisplay = 'auto' | 'paged';

export async function getSponsors(): Promise<{ tiers: SponsorTier[]; display: SponsorDisplay }> {
  const response = await apiRequest<{ sponsors: SponsorTier[]; sponsorDisplay?: SponsorDisplay }>('/api/v1/admin/sponsors');
  return {
    tiers: response.sponsors || [],
    display: response.sponsorDisplay === 'paged' ? 'paged' : 'auto',
  };
}

export async function updateSponsors(sponsors: SponsorTier[], sponsorDisplay: SponsorDisplay = 'auto'): Promise<SponsorTier[]> {
  const response = await apiRequest<{ sponsors: SponsorTier[] }>('/api/v1/admin/sponsors', {
    method: 'PUT',
    body: { sponsors, sponsorDisplay },
  });
  return response.sponsors || sponsors;
}

export interface Affiliation {
  id: string;
  name: string;
  image: string;
  url?: string;
}

export interface CatalogAffiliation {
  id: string;
  name: string;
  image: string;
  url: string;
  category: string;
}

export async function getAffiliations(): Promise<Affiliation[]> {
  const response = await apiRequest<{ affiliations: Affiliation[] }>('/api/v1/admin/affiliations');
  return response.affiliations || [];
}

export async function updateAffiliations(affiliations: Affiliation[]): Promise<Affiliation[]> {
  const response = await apiRequest<{ affiliations: Affiliation[] }>('/api/v1/admin/affiliations', {
    method: 'PUT',
    body: { affiliations },
  });
  return response.affiliations || affiliations;
}

// ── Venues (where we play) ────────────────
export interface Venue {
  id: string;
  name: string;
  addressLine1: string;
  addressLine2: string;
  postcode: string;
  notes: string;
}

export async function getVenues(): Promise<{ venues: Venue[]; limit: number }> {
  const r = await apiRequest<{ venues: Venue[]; limit: number }>('/api/v1/admin/venues');
  return { venues: r.venues || [], limit: r.limit ?? 1 };
}

/** 422 with { error, limit } when the plan's venue cap is exceeded. */
export async function updateVenues(venues: Venue[]): Promise<{ venues: Venue[]; limit: number }> {
  const r = await apiRequest<{ venues: Venue[]; limit: number }>('/api/v1/admin/venues', {
    method: 'PUT',
    body: { venues },
  });
  return { venues: r.venues || venues, limit: r.limit ?? 1 };
}

export async function getAffiliationsCatalog(sport?: string): Promise<CatalogAffiliation[]> {
  const qs = sport ? `?sport=${encodeURIComponent(sport)}` : '';
  const response = await apiRequest<{ catalog: CatalogAffiliation[] }>(`/api/v1/affiliations/catalog${qs}`);
  return response.catalog || [];
}

export async function getTeam(id: string): Promise<Team> {
  const res = await apiRequest<any>(`/api/v1/admin/teams/${id}`);
  return normalizeTeam(res);
}

export async function createTeam(team: Omit<Team, 'id' | 'createdAt' | 'updatedAt'>): Promise<Team> {
  const res = await apiRequest<any>('/api/v1/admin/teams', {
    method: 'POST',
    body: team,
  });
  return normalizeTeam(res);
}

export async function updateTeam(id: string, team: Partial<Team>): Promise<Team> {
  const res = await apiRequest<any>(`/api/v1/admin/teams/${id}`, {
    method: 'PUT',
    body: team,
  });
  return normalizeTeam(res);
}

export async function deleteTeam(id: string): Promise<void> {
  return apiRequest<void>(`/api/v1/admin/teams/${id}`, {
    method: 'DELETE',
  });
}

// ────────────────────────────────────────
// Player profiles (v1.0)
// ────────────────────────────────────────

export type PlayerStatus = 'active' | 'inactive' | 'archived';

export interface PlayerPublicFields {
  showOnSquadPage: boolean;
  showPhoto: boolean;
  showLastName: boolean;
  showJerseyNumber: boolean;
  showPosition: boolean;
  showBio: boolean;
  showAchievements: boolean;
  showStats: boolean;
}

/** One season's totals, keyed by the club's sport stat keys (sportConfig.stats.keys). */
export type PlayerStatsForSeason = Record<string, number>;

export interface PlayerConsent {
  method?: 'verbal' | 'email' | 'signed_form' | 'online_form';
  parentName?: string;
  parentEmail?: string;
  notes?: string;
  fields?: string[];
  documentS3Key?: string;
  // Audit trail. Set by the Lambda, never sent up by the client.
  recordedAt?: string;        // ISO timestamp consent first reached "complete" state
  recordedBy?: string;        // admin email that first recorded it
  lastModifiedAt?: string;    // ISO timestamp of most recent edit after recording
  lastModifiedBy?: string;    // admin email of most recent edit after recording
}

export interface Player {
  clubId: string;
  playerId: string;
  teamId: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  position: string;
  jerseyNumber: number | null;
  photoUrl: string;
  bio: string;
  achievements: string[];
  joinedDate: string;
  status: PlayerStatus;
  publicFields: PlayerPublicFields;
  stats: Record<string, PlayerStatsForSeason>;
  consent: PlayerConsent;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

export async function getPlayers(): Promise<Player[]> {
  const response = await apiRequest<{ players: Player[] }>('/api/v1/admin/players');
  return response.players || [];
}

export async function getPlayersByTeam(teamId: string): Promise<Player[]> {
  const response = await apiRequest<{ players: Player[] }>(`/api/v1/admin/teams/${teamId}/players`);
  return response.players || [];
}

export async function getPlayer(playerId: string): Promise<Player> {
  const response = await apiRequest<{ player: Player }>(`/api/v1/admin/players/${playerId}`);
  return response.player;
}

export async function createPlayer(input: Partial<Player>): Promise<Player> {
  const response = await apiRequest<{ player: Player }>('/api/v1/admin/players', {
    method: 'POST',
    body: input,
  });
  return response.player;
}

export async function updatePlayer(playerId: string, input: Partial<Player>): Promise<void> {
  await apiRequest<void>(`/api/v1/admin/players/${playerId}`, {
    method: 'PUT',
    body: input,
  });
}

/** Soft-delete: marks status='archived', keeps consent record. */
export async function archivePlayer(playerId: string): Promise<void> {
  await apiRequest<void>(`/api/v1/admin/players/${playerId}`, {
    method: 'DELETE',
  });
}

/** Hard-delete: permanently removes player including consent record. Use with care. */
export async function hardDeletePlayer(playerId: string): Promise<void> {
  await apiRequest<void>(`/api/v1/admin/players/${playerId}?hard=true`, {
    method: 'DELETE',
  });
}

// ────────────────────────────────────────
// Members spine (the people: adults AND children, role-tagged)
// Gated server-side on features.members. See lambda/routes/members.ts.
// ────────────────────────────────────────

// `guardian` is the single "parent / guardian" role (no separate `parent`).
export type MemberRole = 'guardian' | 'coach' | 'admin' | 'payer' | 'player' | 'supportStaff';
export type MemberAgeType = 'adult' | 'youth' | 'child';
export type MemberStatus = 'active' | 'archived';

export const MEMBER_ROLES: MemberRole[] = ['guardian', 'coach', 'payer', 'admin', 'player'];
export const ROLE_LABELS: Record<MemberRole, string> = {
  guardian: 'Parent / guardian',
  coach: 'Coach',
  payer: 'Payer',
  admin: 'Admin staff',
  player: 'Player',
  supportStaff: 'Support staff',
};

export interface Member {
  memberId: string;
  name: string;
  email: string;
  phone: string;
  image: string;
  ageType: MemberAgeType;
  roles: MemberRole[];
  coachTitle?: string;
  showEmailPublicly?: boolean;
  staffRole?: string;
  bio?: string;
  status: MemberStatus;
  createdAt: string;
  updatedAt: string;
}

export interface CoachTeamRef {
  teamId: string;
  name: string;
}

/** A coach is a Member (role=coach) with their team assignments resolved. */
export interface CoachMember extends Member {
  teams: CoachTeamRef[];
}

/** One guardian of a player, hydrated with the member's identity. */
export interface Guardian {
  memberId: string;
  role: 'primary' | 'secondary';
  consentMethod: string;
  name: string;
  email: string;
  phone: string;
}

/** Raw player↔guardian link (for the club-wide guardianships join). */
export interface Guardianship {
  playerId: string;
  memberId: string;
  role: 'primary' | 'secondary';
  consentMethod: string;
}

function normalizeMember(raw: any): Member {
  const m = raw?.member ?? raw ?? {};
  return {
    memberId: m.memberId || '',
    name: m.name || '',
    email: m.email || '',
    phone: m.phone || '',
    image: m.image || '',
    ageType: (m.ageType as MemberAgeType) || 'adult',
    roles: Array.isArray(m.roles) ? (m.roles as MemberRole[]) : [],
    coachTitle: m.coachTitle || 'Coach',
    showEmailPublicly: !!m.showEmailPublicly,
    staffRole: m.staffRole || '',
    bio: m.bio || '',
    status: (m.status as MemberStatus) || 'active',
    createdAt: m.createdAt || '',
    updatedAt: m.updatedAt || '',
  };
}

export async function getMembers(role?: string): Promise<Member[]> {
  const qs = role ? `?role=${encodeURIComponent(role)}` : '';
  const response = await apiRequest<{ members: any[] }>(`/api/v1/admin/members${qs}`);
  const members = Array.isArray(response?.members) ? response.members : [];
  return members.map(normalizeMember);
}

export async function getMember(memberId: string): Promise<Member> {
  const res = await apiRequest<any>(`/api/v1/admin/members/${memberId}`);
  return normalizeMember(res);
}

export async function createMember(input: Partial<Member>): Promise<Member> {
  const res = await apiRequest<any>('/api/v1/admin/members', { method: 'POST', body: input });
  return normalizeMember(res);
}

export async function updateMember(memberId: string, input: Partial<Member>): Promise<Member> {
  const res = await apiRequest<any>(`/api/v1/admin/members/${memberId}`, { method: 'PUT', body: input });
  return normalizeMember(res);
}

/** Soft-delete: marks status='archived'. */
export async function archiveMember(memberId: string): Promise<void> {
  await apiRequest<void>(`/api/v1/admin/members/${memberId}`, { method: 'DELETE' });
}

// Guardianship (player ↔ guardian member links)

export async function getGuardians(playerId: string): Promise<Guardian[]> {
  const response = await apiRequest<{ guardians: Guardian[] }>(`/api/v1/admin/players/${playerId}/guardians`);
  return Array.isArray(response?.guardians) ? response.guardians : [];
}

export async function getGuardianships(): Promise<Guardianship[]> {
  const response = await apiRequest<{ guardianships: Guardianship[] }>('/api/v1/admin/guardianships');
  return Array.isArray(response?.guardianships) ? response.guardianships : [];
}

export async function linkGuardian(
  playerId: string,
  memberId: string,
  role: 'primary' | 'secondary' = 'primary',
  consentMethod?: string,
): Promise<void> {
  await apiRequest<void>(`/api/v1/admin/players/${playerId}/guardians`, {
    method: 'POST',
    body: { memberId, role, ...(consentMethod ? { consentMethod } : {}) },
  });
}

export async function unlinkGuardian(playerId: string, memberId: string): Promise<void> {
  await apiRequest<void>(`/api/v1/admin/players/${playerId}/guardians/${memberId}`, { method: 'DELETE' });
}

// Coaches (role-lens over Members; the lambda keeps team.coaches mirrored)

export interface CoachInput {
  memberId?: string;   // promote an existing member to coach
  name?: string;       // or create a new coach
  email?: string;
  phone?: string;
  image?: string;
  coachTitle?: string;
  showEmailPublicly?: boolean;
  teamIds: string[];
}

function normalizeCoach(raw: any): CoachMember {
  const c = raw?.coach ?? raw ?? {};
  return {
    ...normalizeMember(c),
    teams: Array.isArray(c.teams) ? c.teams : [],
  };
}

export async function getCoaches(): Promise<CoachMember[]> {
  const response = await apiRequest<{ coaches: any[] }>('/api/v1/admin/coaches');
  const coaches = Array.isArray(response?.coaches) ? response.coaches : [];
  return coaches.map(normalizeCoach);
}

export async function createCoach(input: CoachInput): Promise<CoachMember> {
  const res = await apiRequest<any>('/api/v1/admin/coaches', { method: 'POST', body: input });
  return normalizeCoach(res);
}

export async function updateCoach(memberId: string, input: Partial<CoachInput>): Promise<CoachMember> {
  const res = await apiRequest<any>(`/api/v1/admin/coaches/${memberId}`, { method: 'PUT', body: input });
  return normalizeCoach(res);
}

/** Removes the coach role + unassigns from all teams. The member persists. */
export async function deleteCoach(memberId: string): Promise<void> {
  await apiRequest<void>(`/api/v1/admin/coaches/${memberId}`, { method: 'DELETE' });
}

// ────────────────────────────────────────
// Club Staff (role-lens over Members; club-wide; Club + Pro only)
// ────────────────────────────────────────

// Staff roles and positions are per sport and come from the API
// (settings.sportConfig), see useSportConfig(). Nothing here holds a list
// of its own except the football fallback below, used only until the first
// settings response arrives or if an older API omits sportConfig.

export interface SupportStaffInput {
  memberId?: string;   // promote an existing member to support staff
  name?: string;
  email?: string;
  phone?: string;
  image?: string;
  staffRole?: string;
  bio?: string;
  showEmailPublicly?: boolean;
}

/** A support-staff member is a Member (role=supportStaff) with staffRole + bio. */
export type SupportStaffMember = Member;

function normalizeStaff(raw: any): SupportStaffMember {
  return normalizeMember(raw?.staff ?? raw ?? {});
}

export async function getSupportStaff(): Promise<SupportStaffMember[]> {
  const response = await apiRequest<{ staff: any[] }>('/api/v1/admin/support-staff');
  const staff = Array.isArray(response?.staff) ? response.staff : [];
  return staff.map(normalizeStaff);
}

export async function createSupportStaff(input: SupportStaffInput): Promise<SupportStaffMember> {
  const res = await apiRequest<any>('/api/v1/admin/support-staff', { method: 'POST', body: input });
  return normalizeStaff(res);
}

export async function updateSupportStaff(memberId: string, input: Partial<SupportStaffInput>): Promise<SupportStaffMember> {
  const res = await apiRequest<any>(`/api/v1/admin/support-staff/${memberId}`, { method: 'PUT', body: input });
  return normalizeStaff(res);
}

export async function deleteSupportStaff(memberId: string): Promise<void> {
  await apiRequest<void>(`/api/v1/admin/support-staff/${memberId}`, { method: 'DELETE' });
}

// ────────────────────────────────────────
// Collections, membership / charge plans
// Gated server-side on features.collections. See lambda/routes/memberships.ts.
// ────────────────────────────────────────

export type MembershipFrequency = 'one-off' | 'annual' | 'monthly' | 'quarterly' | 'season';
export type MembershipVisibility = 'public' | 'admin-only';

export interface Membership {
  membershipId: string;
  name: string;
  totalPrice: number; // pence
  currency: string;
  defaultFrequency: MembershipFrequency;
  allowInstallments: boolean;
  defaultInstallments: number | null;
  installmentRange: { min: number; max: number } | null;
  active: boolean;
  visibility: MembershipVisibility;
  teamId: string;
  ageGroup: string;
  autoSubscribeGroups: string[];
  createdAt: string;
  updatedAt: string;
}

function normalizeMembership(raw: any): Membership {
  const m = raw?.membership ?? raw ?? {};
  return {
    membershipId: m.membershipId || '',
    name: m.name || '',
    totalPrice: Number(m.totalPrice) || 0,
    currency: m.currency || 'GBP',
    defaultFrequency: (m.defaultFrequency as MembershipFrequency) || 'annual',
    allowInstallments: m.allowInstallments === true,
    defaultInstallments: m.defaultInstallments != null ? Number(m.defaultInstallments) : null,
    installmentRange: m.installmentRange || null,
    active: m.active !== false,
    visibility: (m.visibility as MembershipVisibility) || 'admin-only',
    teamId: m.teamId || '',
    ageGroup: m.ageGroup || '',
    autoSubscribeGroups: Array.isArray(m.autoSubscribeGroups) ? m.autoSubscribeGroups : [],
    createdAt: m.createdAt || '',
    updatedAt: m.updatedAt || '',
  };
}

export async function getMemberships(): Promise<Membership[]> {
  const response = await apiRequest<{ memberships: any[] }>('/api/v1/admin/memberships');
  const list = Array.isArray(response?.memberships) ? response.memberships : [];
  return list.map(normalizeMembership);
}

export async function createMembership(input: Partial<Membership>): Promise<Membership> {
  const res = await apiRequest<any>('/api/v1/admin/memberships', { method: 'POST', body: input });
  return normalizeMembership(res);
}

export async function updateMembership(membershipId: string, input: Partial<Membership>): Promise<Membership> {
  const res = await apiRequest<any>(`/api/v1/admin/memberships/${membershipId}`, { method: 'PUT', body: input });
  return normalizeMembership(res);
}

/** Soft-delete: marks active=false. */
export async function deleteMembership(membershipId: string): Promise<void> {
  await apiRequest<void>(`/api/v1/admin/memberships/${membershipId}`, { method: 'DELETE' });
}

/** Payments connection status for the club (Collections). */
/** Can this club collect yet? Gates "Send link" in the Collections screens. */
export interface CollectionsStatus {
  connected: boolean;
  /** Can the club actually charge? Stripe's charges_enabled, not "an account
   *  row exists". False while onboarding is unfinished. */
  verified: boolean;
  detailsSubmitted?: boolean;
  /** How many fields Stripe still wants. 0 when unknown. */
  requirementsDue?: number;
  /** Stripe could not be reached, `verified` is false because we don't know,
   *  not because we checked and it was. */
  statusUnavailable?: boolean;
  clubName?: string;
}
export async function getCollectionsStatus(): Promise<CollectionsStatus> {
  return apiRequest<CollectionsStatus>('/api/v1/admin/collections/status');
}

// ────────────────────────────────────────
// Subscriptions, the payer↔player join (enrol players into a plan).
// Gated server-side on features.collections. See lambda/routes/subscriptions.ts.
// ────────────────────────────────────────

// Collections subscription lifecycle, distinct from the Stripe billing
// `SubscriptionStatus` (the club's own plan) defined later in this file.
export type CollectionStatus = 'pending' | 'active' | 'past_due' | 'paused' | 'cancelled' | 'completed';

export interface Subscription {
  subscriptionId: string;
  playerId: string;
  payerId: string;
  membershipId: string;
  status: CollectionStatus;
  amount: number;          // per-charge pence
  totalAmount: number;     // full plan pence
  frequency: MembershipFrequency | '';
  installmentCount: number | null;
  installmentsPaid: number;
  priceOverride: number | null;
  stripeSubscriptionId: string;
  startedAt: string;
  nextChargeAt: string;
  createdAt: string;
  updatedAt: string;
  // hydrated by the list endpoint
  playerName: string;
  payerName: string;
  payerEmail: string;
  membershipName: string;
}

function normalizeSubscription(raw: any): Subscription {
  const s = raw?.subscription ?? raw ?? {};
  return {
    subscriptionId: s.subscriptionId || '',
    playerId: s.playerId || '',
    payerId: s.payerId || '',
    membershipId: s.membershipId || '',
    status: (s.status as CollectionStatus) || 'pending',
    amount: Number(s.amount) || 0,
    totalAmount: Number(s.totalAmount) || 0,
    frequency: (s.frequency as MembershipFrequency) || '',
    installmentCount: s.installmentCount != null ? Number(s.installmentCount) : null,
    installmentsPaid: Number(s.installmentsPaid) || 0,
    priceOverride: s.priceOverride != null ? Number(s.priceOverride) : null,
    stripeSubscriptionId: s.stripeSubscriptionId || '',
    startedAt: s.startedAt || '',
    nextChargeAt: s.nextChargeAt || '',
    createdAt: s.createdAt || '',
    updatedAt: s.updatedAt || '',
    playerName: s.playerName || '',
    payerName: s.payerName || '',
    payerEmail: s.payerEmail || '',
    membershipName: s.membershipName || '',
  };
}

export interface SubscriptionFilters {
  playerId?: string;
  payerId?: string;
  status?: CollectionStatus;
  membershipId?: string;
}

export async function getSubscriptions(filters: SubscriptionFilters = {}): Promise<Subscription[]> {
  const qs = new URLSearchParams();
  if (filters.playerId) qs.set('playerId', filters.playerId);
  if (filters.payerId) qs.set('payerId', filters.payerId);
  if (filters.status) qs.set('status', filters.status);
  if (filters.membershipId) qs.set('membershipId', filters.membershipId);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const response = await apiRequest<{ subscriptions: any[] }>(`/api/v1/admin/subscriptions${suffix}`);
  const list = Array.isArray(response?.subscriptions) ? response.subscriptions : [];
  return list.map(normalizeSubscription);
}

export interface EnrollmentInput {
  playerId: string;
  membershipId: string;
  payerId?: string;          // defaults to the player's primary guardian
  priceOverride?: number;    // pence
  frequency?: MembershipFrequency;
  installmentCount?: number;
}

export interface EnrollResult {
  created: Subscription[];
  createdCount: number;
  errors: { playerId?: string; error: string }[];
  errorCount: number;
}

export async function enrollPlayers(enrollments: EnrollmentInput[]): Promise<EnrollResult> {
  const res = await apiRequest<any>('/api/v1/admin/collections/enroll', { method: 'POST', body: { enrollments } });
  return {
    created: Array.isArray(res?.created) ? res.created.map(normalizeSubscription) : [],
    createdCount: Number(res?.createdCount) || 0,
    errors: Array.isArray(res?.errors) ? res.errors : [],
    errorCount: Number(res?.errorCount) || 0,
  };
}

export async function updateSubscription(
  subscriptionId: string,
  input: { payerId?: string; priceOverride?: number | null },
): Promise<Subscription> {
  const res = await apiRequest<any>(`/api/v1/admin/subscriptions/${subscriptionId}`, { method: 'PUT', body: input });
  return normalizeSubscription(res);
}

export async function cancelSubscription(subscriptionId: string): Promise<Subscription> {
  const res = await apiRequest<any>(`/api/v1/admin/subscriptions/${subscriptionId}/cancel`, { method: 'POST' });
  return normalizeSubscription(res);
}

/** Email a payer their card-setup link (covers all their pending enrolments). */
export async function sendPaymentLink(payerId: string): Promise<{ ok: boolean; sentTo: string }> {
  return apiRequest<{ ok: boolean; sentTo: string }>('/api/v1/admin/collections/send-link', { method: 'POST', body: { payerId } });
}

// Events Management
export interface Event {
  id: string;
  title: string;
  date: string;
  time?: string;
  location?: string;
  description?: string;
  image?: string;
  url?: string;
  linkText?: string;
  createdAt: string;
  updatedAt: string;
}

export async function getEvents(): Promise<Event[]> {
  const response = await apiRequest<{ events: any[] }>('/api/v1/admin/events');
  const events = Array.isArray(response?.events) ? response.events : [];
  return events.map(ev => ({
    id: ev.id,
    title: ev.title || '',
    date: ev.date || '',
    time: ev.time || '',
    location: ev.location || '',
    description: ev.description || '',
    createdAt: ev.createdAt || '',
    updatedAt: ev.updatedAt || '',
  }));
}

export async function createEvent(data: Omit<Event, 'id' | 'createdAt' | 'updatedAt'>): Promise<Event> {
  const response = await apiRequest<{ event: any }>('/api/v1/admin/events', {
    method: 'POST',
    body: data,
  });
  return response.event;
}

export async function updateEvent(id: string, data: Partial<Omit<Event, 'id' | 'createdAt' | 'updatedAt'>>): Promise<Event> {
  const response = await apiRequest<{ event: any }>(`/api/v1/admin/events/${id}`, {
    method: 'PUT',
    body: data,
  });
  return response.event;
}

export async function deleteEvent(id: string): Promise<void> {
  return apiRequest<void>(`/api/v1/admin/events/${id}`, {
    method: 'DELETE',
  });
}

// Order Management
export interface OrderItem {
  productId: string;
  productName: string;
  quantity: number;
  size: string;
  price: number;
}

export interface Order {
  id: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  items: OrderItem[];
  total: number;
  status: 'received' | 'packed' | 'shipped' | 'delivered' | 'cancelled';
  shippingAddress: {
    street: string;
    city: string;
    postcode: string;
    country: string;
  };
  createdAt: string;
  updatedAt: string;
}

export async function getOrders(): Promise<Order[]> {
  const response = await apiRequest<{ orders: any[] }>('/api/v1/admin/orders');
  const orders = Array.isArray(response?.orders) ? response.orders : [];
  return orders.map(order => ({
    id: order.order_id || order.id,
    customerName: order.customer_name || order.customerName || 'Unknown',
    customerEmail: order.customer_email || order.customerEmail || '',
    customerPhone: order.customer_phone || order.customerPhone || '',
    items: Array.isArray(order.items) ? order.items.map((item: any) => ({
      productId: item.product_id || item.productId,
      productName: item.product_name || item.productName || 'Unknown Product',
      quantity: item.quantity || 1,
      size: item.size || '',
      price: item.price || 0,
    })) : [],
    total: order.total || 0,
    status: order.status || 'received',
    shippingAddress: order.shipping_address || order.shippingAddress || {},
    createdAt: order.created_at || order.createdAt || '',
    updatedAt: order.updated_at || order.updatedAt || '',
  }));
}

export async function getOrder(id: string): Promise<Order> {
  return apiRequest<Order>(`/api/v1/admin/orders/${id}`);
}

export async function updateOrder(id: string, updates: Partial<Order>): Promise<Order> {
  return apiRequest<Order>(`/api/v1/admin/orders/${id}`, {
    method: 'PUT',
    body: updates,
  });
}

// Settings Management
// 'classic' is a UI-only label meaning "no theme override", the picker
// sends `themeName: null` to the API which REMOVEs the field from DDB.
// Persisted theme values: 'matchday' (dark/parallax) and 'newsfeed'
// (Cambridge-style light). Add new values here AND in the lambda
// VALID_THEMES whitelist before exposing them in the picker.
export type ThemeName = 'matchday' | 'newsfeed' | null;

export interface ThemeColors {
  primaryColor: string;   // hex #rrggbb
  secondaryColor: string; // hex #rrggbb
}

// ── Sport ──
// Mirrors lambda/lib/sports.ts ResolvedSportConfig. The SPA cannot import
// from lambda/, so the shape is repeated here and the API is the contract.
export type SportId = 'football' | 'rugby-union' | 'rugby-league' | 'hockey' | 'netball' | 'basketball' | 'water-polo' | 'cricket';
export interface ResolvedSportConfig {
  id: SportId;
  label: string;
  schemaOrgSport: string;
  score: { singular: string; plural: string; max: number };
  drawAllowed: boolean;
  season: { startMonth: number; label: 'split' | 'single' };
  ageCutoff: { month: number; day: number };
  positions: string[];
  staffRoles: string[];
  teamSections: string[];
  resultModel: 'score' | 'innings';
  stats: SportStatsConfig;
}
export interface StatDef { key: string; label: string; short: string }
export interface SportStatsConfig {
  keys: StatDef[];
  reportCounts: string[];
  keeperPositions: string[];
  keeperOnlyCounts: string[];
  cleanSheets: boolean;
  derived: Record<string, Record<string, number>>;
  tiles: { default: string[]; keeper?: string[]; byPosition?: Record<string, string[]> };
}
/** Football, exactly as the platform behaved before the sport field existed. */
export const DEFAULT_SPORT_CONFIG: ResolvedSportConfig = {
  id: 'football',
  label: 'Football',
  schemaOrgSport: 'Football',
  score: { singular: 'goal', plural: 'goals', max: 99 },
  drawAllowed: true,
  season: { startMonth: 8, label: 'split' },
  ageCutoff: { month: 8, day: 31 },
  positions: ['Goalkeeper', 'Defender', 'Midfielder', 'Forward', 'Utility'],
  staffRoles: [
    'President', 'Honours President', 'Chairman', 'Vice-Chairman', 'Secretary', 'Treasurer', 'Club Administrator', 'Board Member',
    'Director of Football', 'First Team Manager', 'Second Team Manager', 'Assistant Manager', 'Coaching Advisor', 'Fitness Coach', 'Strength & Conditioning Coach', 'Sports Scientist', 'Video Analyst',
    'Photographer', 'Journalist / Media', 'Marketing', 'Social Media', 'Social Media Manager', 'Website Manager',
    'Head of Sports Injury', 'Physiotherapist', 'Sports Therapist', 'Team Doctor / Medic', 'First Aider', 'Kit Manager', 'Groundsperson',
    'Welfare Officer', 'Club Welfare / Safeguarding Officer', 'Volunteer Coordinator', 'Sponsorship / Commercial', 'Financial Grants Manager', 'Bid Writer Manager', 'Evidence & Impact Support Officer',
  ],
  teamSections: ['Seniors', 'Youth', 'Minis', 'Women & Girls', 'Veterans'],
  resultModel: 'score',
  stats: {
    keys: [
      { key: 'appearances', label: 'Appearances', short: 'Apps' },
      { key: 'goals', label: 'Goals', short: 'Goals' },
      { key: 'assists', label: 'Assists', short: 'Assists' },
      { key: 'saves', label: 'Saves', short: 'Saves' },
      { key: 'cleanSheets', label: 'Clean sheets', short: 'Clean Sheets' },
      { key: 'defensiveActions', label: 'Defensive actions', short: 'Def. Actions' },
    ],
    reportCounts: ['goals', 'assists', 'saves', 'defensiveActions'],
    keeperPositions: ['Goalkeeper'],
    keeperOnlyCounts: ['saves'],
    cleanSheets: true,
    derived: {},
    tiles: { default: ['appearances', 'goals', 'assists'], keeper: ['appearances', 'saves', 'cleanSheets'], byPosition: { Defender: ['appearances', 'goals', 'defensiveActions'] } },
  },
};
export async function getSports(): Promise<{ id: SportId; label: string }[]> {
  const response = await apiRequest<{ sports: { id: SportId; label: string }[] }>('/api/v1/sports');
  return response.sports || [];
}

export interface Settings {
  siteName: string;
  /** The club's sport. Drives positions, staff roles, score labels. */
  sport?: SportId;
  /** Resolved server-side from `sport`; read-only. */
  sportConfig?: ResolvedSportConfig;
  siteDescription: string;
  contactEmail: string;
  contactPhone: string;
  socialMedia: {
    facebook?: string;
    twitter?: string;
    instagram?: string;
  };
  maintenanceMode: boolean;
  /** Team cards: 'all' shows every coach (default), 'lead' shows the most senior only. */
  teamCardCoaches?: 'all' | 'lead';
  updatedAt: string;
  gaTrackingId?: string;
  themeName?: ThemeName;
  plan?: string;
  /** Brand colours editable by all tiers. Server derives the 4
   *  light/dark variants from these 2 via HSL adjustment. */
  themeColors?: ThemeColors;
  /** Whether a badge image exists, drives "Reset to badge colours"
   *  button enable state. */
  hasBadge?: boolean;
  /** Raw feature flags (what exists / auto-toggled by content). */
  features?: Record<string, boolean>;
  /** Admin-hidden sections, the public site renders features MINUS these.
   *  Keys are validated server-side against the hideable set. */
  hiddenFeatures?: Record<string, boolean>;
}

export async function getSettings(): Promise<Settings> {
  const response = await apiRequest<{ settings: Record<string, unknown> }>('/api/v1/admin/settings');
  const settings = response?.settings || {};
  const rawTheme = settings.themeName as string | undefined;
  const tc = (settings.themeColors as ThemeColors | undefined) || undefined;
  return {
    siteName: (settings.siteName as string) || 'Girton FC',
    siteDescription: (settings.siteDescription as string) || '',
    contactEmail: (settings.contactEmail as string) || '',
    contactPhone: (settings.contactPhone as string) || '',
    socialMedia: (settings.socialMedia as Settings['socialMedia']) || {},
    maintenanceMode: (settings.maintenanceMode as boolean) || false,
    // Must be listed here. This normalizer builds an EXPLICIT object, so a field
    // the server returns but this function forgets is silently dropped, the form
    // then reads it back as undefined, falls to the default, and the next save
    // writes that default over the user's real choice. Whiteknights hit exactly
    // that within a day of this setting shipping ("go back to settings and it's
    // always reverted to show every coach"). Same failure as normalizeTeam.
    teamCardCoaches: (settings.teamCardCoaches === 'lead' ? 'lead' : 'all'),
    updatedAt: (settings.updatedAt as string) || '',
    gaTrackingId: (settings.gaTrackingId as string) || '',
    // 'matchday' and 'newsfeed' are recognised themes. Anything else
    // (unset, '', unknown value) maps to null = Classic / default.
    themeName: (rawTheme === 'matchday' || rawTheme === 'newsfeed') ? rawTheme : null,
    plan: (settings.plan as string) || 'free',
    themeColors: tc
      ? { primaryColor: tc.primaryColor || '#22c55e', secondaryColor: tc.secondaryColor || '#1a1a1a' }
      : { primaryColor: '#22c55e', secondaryColor: '#1a1a1a' },
    hasBadge: !!settings.hasBadge,
    features: (settings.features as Record<string, boolean>) || {},
    hiddenFeatures: (settings.hiddenFeatures as Record<string, boolean>) || {},
    sport: (settings.sport as SportId) || 'football',
    sportConfig: (settings.sportConfig as ResolvedSportConfig) || DEFAULT_SPORT_CONFIG,
  };
}

export async function updateSettings(settings: Partial<Settings> & {
  /** Reset path: server re-runs colour extraction on the current badge. */
  resetThemeColors?: boolean;
}): Promise<Settings> {
  const res = await apiRequest<Settings>('/api/v1/admin/settings', {
    method: 'PUT',
    body: settings,
  });
  window.dispatchEvent(new Event('preview-changed'));
  return res;
}

/** Convenience: re-extract colours from the club's current badge image. */
export async function resetThemeColorsFromBadge(): Promise<Settings> {
  return updateSettings({ resetThemeColors: true });
}

/** GDPR portability: the club's full data as a JSON bundle. */
export async function exportClubData(): Promise<Record<string, unknown>> {
  return apiRequest<Record<string, unknown>>('/api/v1/admin/export');
}

/** Self-service full deletion, permanent. Server requires the literal
 *  confirm string and an owner-role JWT; the UI adds the are-you-sure. */
export async function deleteAccount(): Promise<void> {
  await apiRequest('/api/v1/admin/account', {
    method: 'DELETE',
    body: { confirm: 'DELETE' },
  });
}

// Image Upload
export interface UploadUrlResponse {
  uploadUrl: string;
  fileUrl: string;
}

export async function getUploadUrl(filename: string, contentType: string, fileSize?: number): Promise<UploadUrlResponse> {
  const res = await apiRequest<{ uploadUrl: string; publicUrl: string }>('/api/v1/admin/upload', {
    method: 'POST',
    body: { filename, contentType, fileSize },
  });
  return { uploadUrl: res.uploadUrl, fileUrl: res.publicUrl };
}

export async function uploadImage(file: File): Promise<string> {
  const { uploadUrl, fileUrl } = await getUploadUrl(file.name, file.type, file.size);

  // CRITICAL: check the PUT actually succeeded before returning fileUrl.
  // fetch() only rejects on network failure, an HTTP 4xx/5xx from S3
  // (expired presign, size mismatch, transient 503) resolves as a
  // normal Response. Without this guard a failed upload was treated as
  // success and the caller persisted a fileUrl pointing at a key that
  // was never stored. That's the kenningwell dead-ref incident
  // (2026-05-31): 3 news images "saved" to content rows but absent
  // from S3. A presigned PUT returning 200 IS S3's durable-write
  // confirmation (read-after-write consistency for new objects), so
  // res.ok is the complete fix, no separate HEAD needed.
  const putRes = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });
  if (!putRes.ok) {
    throw new Error(
      `Upload failed (HTTP ${putRes.status}). Your image was not saved, please try again.`,
    );
  }

  // Extract S3 key from the public URL for moderation
  // URL format: https://bucket.s3.amazonaws.com/clubId/timestamp-random.ext
  const urlParts = new URL(fileUrl);
  const key = urlParts.pathname.slice(1); // remove leading /

  // Run Rekognition moderation. Block the upload if it's rejected, the server
  // has already deleted the object, so saving its URL would leave a dead ref
  // (a broken image on the live site). moderateUpload fails OPEN on infra /
  // network errors so a moderation hiccup never blocks a legit upload.
  const moderation = await moderateUpload('images', key);
  if (!moderation.safe) {
    throw new Error(moderation.message || "This image can't be used, please choose a different photo.");
  }

  return fileUrl;
}

// Badge management
export async function getBadgeUploadUrl(filename: string, contentType: string): Promise<{ uploadUrl: string; publicUrl: string; key: string }> {
  return apiRequest('/api/v1/admin/badge/upload', {
    method: 'POST',
    body: { filename, contentType },
  });
}

export async function updateBadge(badgeUrl: string): Promise<{ badgeUrl: string }> {
  return apiRequest('/api/v1/admin/badge', {
    method: 'PUT',
    body: { badgeUrl },
  });
}

export async function moderateUpload(
  bucket: string,
  key: string,
): Promise<{ safe: boolean; message?: string }> {
  // Own fetch (not apiRequest) so we can distinguish a real rejection (400 →
  // surface the server's friendly message + block) from a moderation-endpoint
  // failure (network / 5xx → fail open, don't block the upload).
  const token = localStorage.getItem('authToken');
  try {
    const res = await fetch(`${API_URL}/api/v1/admin/upload/moderate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ bucket, key }),
    });
    if (res.ok) return { safe: true };
    if (res.status === 400) {
      const b = await res.json().catch(() => ({} as any));
      return {
        safe: false,
        message: b.error || "This image can't be used, it didn't pass our content check. Please choose a different photo.",
      };
    }
    return { safe: true }; // other status → infra problem → fail open
  } catch {
    return { safe: true }; // network failure → fail open
  }
}

// Gallery Management
export interface Album {
  albumId: string;
  title: string;
  description: string;
  coverImage: string;
  images: string[];
  imageCount?: number;
  createdAt: string;
  updatedAt: string;
  hiddenFromPublic?: boolean;
}

export interface GalleryResponse {
  albums: Album[];
  plan?: string;
  albumLimit?: number | null;
  imageLimit?: number | null;
  hiddenCount?: number;
}

export async function getGallery(): Promise<GalleryResponse> {
  const response = await apiRequest<{ albums: any[]; plan?: string; albumLimit?: number | null; imageLimit?: number | null; hiddenCount?: number }>('/api/v1/admin/gallery');
  const albums = Array.isArray(response?.albums) ? response.albums : [];
  return {
    albums: albums.map(album => ({
      albumId: album.albumId || album.album_id || '',
      title: album.title || '',
      description: album.description || '',
      coverImage: album.coverImage || album.cover_image || '',
      images: Array.isArray(album.images) ? album.images : [],
      imageCount: album.imageCount ?? album.image_count ?? (Array.isArray(album.images) ? album.images.length : 0),
      createdAt: album.createdAt || album.created_at || '',
      updatedAt: album.updatedAt || album.updated_at || '',
      hiddenFromPublic: album.hiddenFromPublic === true,
    })),
    plan: response?.plan,
    albumLimit: response?.albumLimit,
    imageLimit: response?.imageLimit,
    hiddenCount: response?.hiddenCount || 0,
  };
}

export async function createAlbum(data: { title: string; description?: string; coverImage?: string; images?: string[] }): Promise<Album> {
  const response = await apiRequest<{ album: any }>('/api/v1/admin/gallery', {
    method: 'POST',
    body: data,
  });
  const album = response.album;
  return {
    albumId: album.albumId || '',
    title: album.title || '',
    description: album.description || '',
    coverImage: album.coverImage || '',
    images: Array.isArray(album.images) ? album.images : [],
    imageCount: Array.isArray(album.images) ? album.images.length : 0,
    createdAt: album.createdAt || '',
    updatedAt: album.updatedAt || '',
  };
}

export async function updateAlbum(albumId: string, data: Partial<{ title: string; description: string; coverImage: string; images: string[] }>): Promise<void> {
  await apiRequest<void>(`/api/v1/admin/gallery/${albumId}`, {
    method: 'PUT',
    body: data,
  });
}

export async function deleteAlbum(albumId: string): Promise<void> {
  return apiRequest<void>(`/api/v1/admin/gallery/${albumId}`, {
    method: 'DELETE',
  });
}

// Fixtures & Results (pilot, endpoints 404 unless the club's fixtures flag is on)
export type FixtureStatus = 'scheduled' | 'played' | 'postponed' | 'cancelled' | 'abandoned';

/** Cricket: one innings of runs/wickets/overs for one side. */
export interface Innings { side: 'us' | 'them'; runs: number; wickets: number; overs?: number; declared?: boolean; forfeited?: boolean }
export type InningsOutcome = 'won' | 'lost' | 'tied' | 'drawn' | 'no-result' | 'abandoned' | 'conceded' | 'walkover';
export interface InningsResult { outcome: InningsOutcome; marginBy?: 'runs' | 'wickets'; margin?: number; dls?: boolean }
/** Computed by the API for every fixture: the chip, the scoreline and a sentence. */
export interface FixtureSummary { outcome: 'W' | 'L' | 'D' | 'T' | 'NR' | null; scoreline: string; description: string }

export interface Fixture {
  fixtureId: string;
  teamId?: string;
  date: string;      // YYYY-MM-DD
  koTime?: string;   // HH:MM
  homeAway: 'home' | 'away';
  opponent: string;
  competition: string;
  status: FixtureStatus;
  goalsFor?: number;
  goalsAgainst?: number;
  innings?: Innings[];
  result?: InningsResult;
  summary?: FixtureSummary;
  notes?: string;
  /** Players of the Match: up to three, stored as ids (older rows hold one object). */
  potm?: FixturePotm[] | FixturePotm;
  photo?: { url: string; caption?: string };
  createdAt: string;
  updatedAt: string;
}

export interface FixturePotm { playerId: string; source?: string; setAt?: string }

/** The award ids on a fixture, whichever shape the row was written in. */
export function potmIdsOf(f: Pick<Fixture, 'potm'>): string[] {
  const list = Array.isArray(f.potm) ? f.potm : f.potm ? [f.potm] : [];
  return list.map(p => p?.playerId).filter((id): id is string => !!id);
}

/** Most Players of the Match on one fixture. Mirrors lambda/lib/potm.ts. */
export const MAX_POTM = 3;

export interface FixtureInput {
  teamId?: string;
  date: string;
  koTime?: string;
  homeAway: 'home' | 'away';
  opponent: string;
  competition: string;
  status: FixtureStatus;
  goalsFor?: number;
  goalsAgainst?: number;
  innings?: Innings[];
  result?: InningsResult;
  notes?: string;
  /** Absent: leave the stored awards alone. Empty: clear them. */
  potmPlayerIds?: string[];
}

export interface Registration {
  registrationId: string;
  status: 'verified' | 'accepted' | 'rejected';
  playerFirstName: string;
  playerLastName?: string;
  dateOfBirth?: string;
  guardianName: string;
  guardianEmail: string;
  guardianPhone?: string;
  teamId?: string;
  notes?: string;
  /** Snapshot of the club's own questions as answered, label included. */
  answers?: Array<{ id: string; label: string; value: string }>;
  createdAt?: string;
  verifiedAt?: string;
  acceptedAt?: string;
  playerId?: string;
}

/** Unverified submissions are withheld server-side, this never returns them. */
export async function getRegistrations(): Promise<Registration[]> {
  const response = await apiRequest<{ registrations: Registration[] }>('/api/v1/admin/registrations');
  return Array.isArray(response?.registrations) ? response.registrations : [];
}

export async function acceptRegistration(id: string, teamId?: string): Promise<{ playerId: string }> {
  return apiRequest<{ playerId: string }>(`/api/v1/admin/registrations/${id}/accept`, {
    method: 'POST',
    body: { teamId: teamId || '' },
  });
}

export async function rejectRegistration(id: string): Promise<{ message: string }> {
  return apiRequest<{ message: string }>(`/api/v1/admin/registrations/${id}/reject`, { method: 'POST' });
}

export async function getFixtures(): Promise<Fixture[]> {
  const response = await apiRequest<{ fixtures: Fixture[] }>('/api/v1/admin/fixtures');
  return Array.isArray(response?.fixtures) ? response.fixtures : [];
}

export async function createFixture(data: FixtureInput): Promise<Fixture> {
  const response = await apiRequest<{ fixture: Fixture }>('/api/v1/admin/fixtures', {
    method: 'POST',
    body: data,
  });
  return response.fixture;
}

export async function updateFixture(id: string, data: FixtureInput): Promise<Fixture> {
  const response = await apiRequest<{ fixture: Fixture }>(`/api/v1/admin/fixtures/${id}`, {
    method: 'PUT',
    body: data,
  });
  return response.fixture;
}

export async function deleteFixture(id: string): Promise<void> {
  return apiRequest<void>(`/api/v1/admin/fixtures/${id}`, { method: 'DELETE' });
}

// Fixtures auto-sync from FA Full-Time
export interface FixturesSyncFeed { fullTimeTeamId: string; teamId: string; }
export interface FixturesSyncConfig {
  enabled: boolean;
  feeds: FixturesSyncFeed[];
  lastSyncedAt: string;
  lastStatus: string;
  lastCount: number;
}
export interface FixturesSyncRunResult {
  ok: boolean;
  changed: boolean;
  fetched: number;
  written: number;
  removed: number;
  error?: string;
}

export async function getFixturesSyncConfig(): Promise<FixturesSyncConfig> {
  const r = await apiRequest<{ sync: FixturesSyncConfig }>('/api/v1/admin/fixtures/sync');
  return r.sync;
}

export async function updateFixturesSyncConfig(
  data: { enabled: boolean; feeds: FixturesSyncFeed[] },
): Promise<FixturesSyncConfig> {
  const r = await apiRequest<{ sync: FixturesSyncConfig }>('/api/v1/admin/fixtures/sync', { method: 'PUT', body: data });
  return r.sync;
}

export async function syncFixturesNow(
  feeds?: FixturesSyncFeed[],
): Promise<FixturesSyncRunResult> {
  const r = await apiRequest<{ result: FixturesSyncRunResult }>('/api/v1/admin/fixtures/sync/run', {
    method: 'POST',
    body: feeds ? { feeds } : {},
  });
  return r.result;
}

// ────────────────────────────────────────
// Play-Cricket sync, cricket clubs only. The API key is write-only: the
// server says whether one is saved (hasKey) and never returns it.
// ────────────────────────────────────────

export interface PlayCricketTeamLink { pcTeamId: string; teamId: string; pcTeamName?: string }

export interface PlayCricketConfig {
  siteId: string;
  enabled: boolean;
  hasKey: boolean;
  teams: PlayCricketTeamLink[];
  lastSyncedAt: string;
  lastStatus: string;
  lastCount: number;
}

export interface PlayCricketSyncResult {
  ok: boolean;
  changed: boolean;
  fetched: number;
  written: number;
  removed: number;
  unmapped: number;
  error?: string;
}

export async function getPlayCricketConfig(): Promise<PlayCricketConfig> {
  const r = await apiRequest<{ playCricket: PlayCricketConfig }>('/api/v1/admin/play-cricket');
  return r.playCricket;
}

export async function updatePlayCricketConfig(input: {
  siteId?: string; apiKey?: string; removeKey?: boolean; enabled?: boolean; teams?: PlayCricketTeamLink[];
}): Promise<PlayCricketConfig> {
  const r = await apiRequest<{ playCricket: PlayCricketConfig }>('/api/v1/admin/play-cricket', { method: 'PUT', body: input });
  return r.playCricket;
}

export async function getPlayCricketTeams(): Promise<Array<{ id: string; name: string }>> {
  const r = await apiRequest<{ teams: Array<{ id: string; name: string }> }>('/api/v1/admin/play-cricket/teams');
  return r.teams;
}

export async function syncPlayCricketNow(): Promise<PlayCricketSyncResult> {
  const r = await apiRequest<{ result: PlayCricketSyncResult }>('/api/v1/admin/play-cricket/sync', { method: 'POST', body: {} });
  return r.result;
}

// Document Management
export type DocCategory = 'general' | 'safeguarding' | 'policies' | 'minutes' | 'forms' | 'other';

export interface Document {
  docId: string;
  title: string;
  description?: string;
  category: DocCategory;
  fileUrl: string;
  fileName?: string;
  fileSize?: number;
  createdAt: string;
  updatedAt: string;
  hiddenFromPublic?: boolean;
}

export interface DocumentsResponse {
  documents: Document[];
  count: number;
  limit: number | null;
  plan: string;
  hiddenCount: number;
}

export async function getDocuments(): Promise<DocumentsResponse> {
  const response = await apiRequest<DocumentsResponse & { hiddenCount?: number }>('/api/v1/admin/documents');
  return {
    documents: Array.isArray(response?.documents) ? response.documents : [],
    count: response?.count || 0,
    limit: response?.limit ?? null,
    plan: response?.plan || 'free',
    hiddenCount: response?.hiddenCount || 0,
  };
}

export async function createDocument(data: {
  title: string;
  description?: string;
  category?: DocCategory;
  fileUrl: string;
  fileName?: string;
  fileSize?: number;
}): Promise<Document> {
  const response = await apiRequest<{ document: Document }>('/api/v1/admin/documents', {
    method: 'POST',
    body: data,
  });
  return response.document;
}

export async function updateDocument(
  id: string,
  data: { title?: string; description?: string; category?: DocCategory },
): Promise<Document> {
  const response = await apiRequest<{ document: Document }>(`/api/v1/admin/documents/${id}`, {
    method: 'PUT',
    body: data,
  });
  return response.document;
}

export async function deleteDocument(id: string): Promise<void> {
  return apiRequest<void>(`/api/v1/admin/documents/${id}`, { method: 'DELETE' });
}

export async function uploadFile(file: File): Promise<string> {
  const { uploadUrl, fileUrl } = await getUploadUrl(file.name, file.type, file.size);

  // Same guard as uploadImage, fetch() resolves on HTTP errors, so an
  // unchecked PUT lets a failed upload masquerade as success and
  // persist a dead URL. See uploadImage for the full rationale.
  const putRes = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });
  if (!putRes.ok) {
    throw new Error(
      `Upload failed (HTTP ${putRes.status}). Your file was not saved, please try again.`,
    );
  }

  return fileUrl;
}

// Dashboard Stats
export interface DashboardStats {
  teamCount: number;
  inquiryCount: number;
}

export async function getDashboardStats(): Promise<DashboardStats> {
  const response = await apiRequest<DashboardStats>('/api/v1/admin/dashboard');
  return {
    teamCount: response.teamCount || 0,
    inquiryCount: response.inquiryCount || 0,
  };
}

// Superadmin
export interface ClubSummary {
  clubId: string;
  clubName: string;
  status: string;
  plan: string;
  createdAt: string;
}

export async function listAllClubs(): Promise<ClubSummary[]> {
  const response = await apiRequest<{ clubs: ClubSummary[] }>('/api/v1/admin/clubs');
  return response.clubs || [];
}

export async function deleteClubById(clubId: string): Promise<void> {
  return apiRequest<void>(`/api/v1/admin/clubs/${clubId}`, { method: 'DELETE' });
}

// Stripe Connect
export interface StripeConnectStatus {
  connected: boolean;
  chargesEnabled?: boolean;
  payoutsEnabled?: boolean;
  detailsSubmitted?: boolean;
}

export async function createStripeConnect(): Promise<{ url: string }> {
  return apiRequest<{ url: string }>('/api/v1/admin/stripe/connect', { method: 'POST' });
}

export async function getStripeConnectStatus(): Promise<StripeConnectStatus> {
  return apiRequest<StripeConnectStatus>('/api/v1/admin/stripe/status');
}

export async function createStripeAccountLink(): Promise<{ url: string }> {
  return apiRequest<{ url: string }>('/api/v1/admin/stripe/account-link', { method: 'POST' });
}

export async function getStripeDashboardLink(): Promise<{ url: string }> {
  return apiRequest<{ url: string }>('/api/v1/admin/stripe/dashboard');
}

// Stripe Subscriptions
export interface SubscriptionStatus {
  plan: string;
  subscriptionStatus: string | null;
  currentPeriodEnd: string | null;
  stripeCustomerId: string | null;
  hasSubscription: boolean;
}

export async function createSubscriptionCheckout(plan: string): Promise<{ url: string }> {
  return apiRequest<{ url: string }>('/api/v1/admin/stripe/subscribe', {
    method: 'POST',
    body: { plan },
  });
}

export async function createCustomerPortalSession(): Promise<{ url: string }> {
  return apiRequest<{ url: string }>('/api/v1/admin/stripe/portal', { method: 'POST' });
}

export async function getSubscriptionStatus(): Promise<SubscriptionStatus> {
  return apiRequest<SubscriptionStatus>('/api/v1/admin/stripe/subscription');
}

// Admin User Management

export interface AdminUser {
  email: string;
  name: string;
  role: 'owner' | 'admin';
  createdAt: string;
  forcePasswordChange: boolean;
}

export interface AdminUsersResponse {
  admins: AdminUser[];
  count: number;
  limit: number;
  plan: string;
}

export async function getAdminUsers(): Promise<AdminUsersResponse> {
  const response = await apiRequest<AdminUsersResponse>('/api/v1/admin/admins');
  return {
    admins: Array.isArray(response?.admins) ? response.admins : [],
    count: response?.count || 0,
    limit: response?.limit || 1,
    plan: response?.plan || 'free',
  };
}

export async function inviteAdminUser(email: string, name: string): Promise<AdminUser> {
  const response = await apiRequest<{ admin: AdminUser }>('/api/v1/admin/admins/invite', {
    method: 'POST',
    body: { email, name },
  });
  return response.admin;
}

export async function removeAdminUser(email: string): Promise<void> {
  return apiRequest<void>(`/api/v1/admin/admins/${encodeURIComponent(email)}`, {
    method: 'DELETE',
  });
}

// ─────────────────────────────────────────────
// Email Sending (v1, ultra-lean, paste-list-and-send)
// ─────────────────────────────────────────────

export interface EmailEngagementStats {
  unique: number;
  total: number;
  lastEventAt?: string;
}

export interface EmailSend {
  sendId: string;
  subject: string;
  recipientCount: number;
  sentAt: string;
  sentBy: string;
  stats: {
    queued: number;
    delivered: number;
    bounced: number;
    complained: number;
    unsubscribed: number;
    // Phase 3a analytics fields, only populated when the club's
    // features.email_analytics flag is true. Lambda strips them for
    // unflagged clubs.
    opens?: EmailEngagementStats;
    clicks?: EmailEngagementStats;
    topLinks?: Record<string, number>;
  };
}

export interface EmailQuota {
  monthlySendLimit: number | null; // null = unlimited (Pro)
  usedThisMonth: number;
}

export interface ListSendsResponse {
  sends: EmailSend[];
  plan: string;
  quota: EmailQuota;
  // True when the club has features.email_analytics enabled.
  // SPA uses this to decide whether to render the analytics columns/cards.
  emailAnalyticsEnabled?: boolean;
  /** False when platform sending is switched off (CLUB_EMAIL_SENDING). */
  sendingEnabled?: boolean;
}

export interface RecipientEngagementEntry {
  opens?: { count: number; firstAt: string; lastAt: string };
  clicks?: { count: number; firstAt: string; lastAt: string };
}

export interface GetSendResponse {
  send: any;
  emailAnalyticsEnabled?: boolean;
  // Per-recipient engagement breakdown, populated only when
  // emailAnalyticsEnabled. Keyed by lowercased recipient email.
  recipientEngagement?: Record<string, RecipientEngagementEntry>;
}

export async function listEmailSends(): Promise<ListSendsResponse> {
  return apiRequest<ListSendsResponse>('/api/v1/admin/email/sends');
}

export async function getEmailSend(sendId: string): Promise<GetSendResponse> {
  return apiRequest<GetSendResponse>(`/api/v1/admin/email/sends/${encodeURIComponent(sendId)}`);
}

export interface ValidateRecipientsResponse {
  validCount: number;
  invalidCount: number;
  suppressedCount: number;
  sendableCount: number;
  invalidEmails: string[];
  suppressedEmails: string[];
  error?: string;
}

export async function validateRecipients(recipients: string): Promise<ValidateRecipientsResponse> {
  return apiRequest<ValidateRecipientsResponse>('/api/v1/admin/email/validate', {
    method: 'POST',
    body: { recipients },
  });
}

export async function previewEmail(bodyHtml: string): Promise<{ html: string }> {
  return apiRequest<{ html: string }>('/api/v1/admin/email/preview', {
    method: 'POST',
    body: { bodyHtml },
  });
}

export interface SendEmailRequest {
  recipients: string;
  subject: string;
  bodyHtml: string;
  replyTo?: string;
  sendMeCopy?: boolean;
  consentConfirmed: boolean;
  /** 'groups' = send to all subscribers in the selected groupIds
   *  (Email v2 Phase 2, Pro-only). 'list' = send to all active
   *  subscribers from the stored list (Email v2 Phase 1). 'paste'
   *  (default) = use the recipients textarea string. */
  mode?: 'groups' | 'list' | 'paste';
  /** Required when mode === 'groups'. Use ALL_MEMBERS_GROUP_ID
   *  ('all-members') to select the synthetic everyone-bucket. */
  groupIds?: string[];
}

/** Synthetic groupId for the auto "All members" group. Matches the
 *  ALL_MEMBERS_GROUP_ID constant in lambda/routes/groups.ts. */
export const ALL_MEMBERS_GROUP_ID = 'all-members';

export interface SendEmailResponse {
  sendId: string;
  stats: { queued: number; delivered: number; bounced: number };
  failedCount: number;
}

export async function sendEmailNow(req: SendEmailRequest): Promise<SendEmailResponse> {
  return apiRequest<SendEmailResponse>('/api/v1/admin/email/send', {
    method: 'POST',
    body: req,
  });
}

/**
 * Send a test email to the logged-in admin's own address.
 * Does NOT consume monthly quota, does NOT appear in send history.
 * Subject gets a [TEST] prefix automatically. Use to preview a draft
 * before broadcasting to the real list.
 */
export async function sendTestEmail(req: {
  subject: string;
  bodyHtml: string;
  replyTo?: string;
}): Promise<{ message: string; sentTo: string }> {
  return apiRequest<{ message: string; sentTo: string }>('/api/v1/admin/email/test', {
    method: 'POST',
    body: req,
  });
}

export interface SuppressionRow {
  email: string;
  reason: 'bounce' | 'complaint' | 'unsubscribe' | 'manual';
  reasonDetail?: string;
  addedAt: string;
  addedBy?: string;
}

export async function listEmailSuppressions(): Promise<{ suppressions: SuppressionRow[] }> {
  return apiRequest<{ suppressions: SuppressionRow[] }>('/api/v1/admin/email/suppressions');
}

export async function addEmailSuppression(email: string, reason: string = 'manual'): Promise<void> {
  return apiRequest<void>('/api/v1/admin/email/suppressions', {
    method: 'POST',
    body: { email, reason },
  });
}

export async function removeEmailSuppression(email: string): Promise<void> {
  return apiRequest<void>(`/api/v1/admin/email/suppressions/${encodeURIComponent(email)}`, {
    method: 'DELETE',
  });
}

// ─────────────────────────────────────────
// Upload telemetry, fire-and-forget beacon when the client-side
// rejects a file BEFORE it ever hits the upload endpoint (e.g.
// oversized). Without this, those rejections were invisible to
// ops, see the 2026-05-30 kenningwell incident.
// ─────────────────────────────────────────

export interface RejectedUploadBeacon {
  clubId: string;
  filename: string;
  size: number;
  mime?: string;
  reason: string;
  plan?: string;
  cap?: number;
}

/**
 * Delete uploaded-but-unsaved S3 objects (orphan cleanup).
 * Called when an admin closes a modal that has just-uploaded photos
 * without clicking Save. Backend verifies each URL belongs to this
 * club before deleting; the storageUsedBytes counter is auto-decremented.
 * Best-effort, never blocks UX.
 */
export async function deleteUploads(urls: string[]): Promise<{ deleted: number; freedBytes: number; skipped: number }> {
  return apiRequest<{ deleted: number; freedBytes: number; skipped: number }>(
    '/api/v1/admin/upload/delete',
    { method: 'POST', body: { urls } },
  );
}

export async function reportRejectedUpload(payload: RejectedUploadBeacon): Promise<void> {
  // Best-effort, no error handling, the user already saw the
  // rejection message in the SPA; we just want CloudWatch breadcrumbs.
  const apiBase = (import.meta as any).env?.VITE_API_URL || 'https://api.allgrassroots.invalid';
  try {
    await fetch(`${apiBase}/api/v1/upload/rejected-beacon`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      // Keep request alive even if user navigates away, beacon is
      // a one-shot fire from a transient UI moment.
      keepalive: true,
    });
  } catch {
    // swallow, beacon is non-essential
  }
}

// ─────────────────────────────────────────
// Subscribers (Email v2 Phase 1), persistent per-club mailing list.
// Distinct from Suppressions (people we WON'T send to).
// ─────────────────────────────────────────

export interface Subscriber {
  email: string;
  name: string;
  status: 'active' | 'deleted';
  addedAt: string;
  addedBy: string;
  source: 'manual' | 'csv' | 'join-form' | 'contact-form' | 'import-from-send';
  /** Email v2 Phase 2: list of groupIds this subscriber belongs to.
   *  Empty array on Club tier (groups are Pro-only). */
  groups: string[];
  deletedAt?: string;
  deletedBy?: string;
}

export interface SubscribersResponse {
  subscribers: Subscriber[];
  count: number;
}

export async function listSubscribers(opts?: {
  status?: 'active' | 'deleted' | 'all';
  search?: string;
}): Promise<SubscribersResponse> {
  const params = new URLSearchParams();
  if (opts?.status) params.set('status', opts.status);
  if (opts?.search) params.set('search', opts.search);
  const qs = params.toString();
  return apiRequest<SubscribersResponse>(
    `/api/v1/admin/subscribers${qs ? '?' + qs : ''}`,
  );
}

export interface AddSubscriberResult {
  added: number;
  addedEmails: string[];
  skippedExisting: number;
  skippedExistingEmails: string[];
  blockedDeleted: number;
  blockedDeletedEmails: string[];
  invalid: number;
  invalidEmails: string[];
}

export async function addSubscriberSingle(
  email: string,
  name?: string,
): Promise<AddSubscriberResult> {
  return apiRequest<AddSubscriberResult>('/api/v1/admin/subscribers', {
    method: 'POST',
    body: { email, name, source: 'manual' },
  });
}

export async function addSubscribersBulk(
  emails: string,
): Promise<AddSubscriberResult> {
  return apiRequest<AddSubscriberResult>('/api/v1/admin/subscribers', {
    method: 'POST',
    body: { emails, source: 'manual' },
  });
}

export async function updateSubscriberName(
  email: string,
  name: string,
): Promise<void> {
  return apiRequest<void>(
    `/api/v1/admin/subscribers/${encodeURIComponent(email)}`,
    { method: 'PUT', body: { name } },
  );
}

export async function deleteSubscriber(email: string): Promise<void> {
  return apiRequest<void>(
    `/api/v1/admin/subscribers/${encodeURIComponent(email)}`,
    { method: 'DELETE' },
  );
}

// ─────────────────────────────────────────
// Groups (Email v2 Phase 2, Pro-only)
// Returns 403 for Club tier; the SPA hides the groups UI client-side
// based on plan from useAuth() so the endpoint is the backstop, not
// the only line of defence.
// ─────────────────────────────────────────

export interface EmailGroup {
  groupId: string;
  name: string;
  memberCount: number;
  createdAt: string;
  isAuto?: boolean;  // true for the synthetic "All members" group
}

export async function listGroups(): Promise<{ groups: EmailGroup[]; count: number }> {
  return apiRequest<{ groups: EmailGroup[]; count: number }>('/api/v1/admin/groups');
}

export async function createGroup(name: string): Promise<{ group: EmailGroup }> {
  return apiRequest<{ group: EmailGroup }>('/api/v1/admin/groups', {
    method: 'POST',
    body: { name },
  });
}

export async function renameGroup(groupId: string, name: string): Promise<void> {
  return apiRequest<void>(`/api/v1/admin/groups/${encodeURIComponent(groupId)}`, {
    method: 'PATCH',
    body: { name },
  });
}

export async function deleteGroup(groupId: string): Promise<void> {
  return apiRequest<void>(`/api/v1/admin/groups/${encodeURIComponent(groupId)}`, {
    method: 'DELETE',
  });
}

export async function setSubscriberGroups(
  email: string,
  groupIds: string[],
): Promise<{ ok: boolean; groups: string[] }> {
  return apiRequest<{ ok: boolean; groups: string[] }>(
    `/api/v1/admin/subscribers/${encodeURIComponent(email)}/groups`,
    { method: 'PUT', body: { groupIds } },
  );
}

export async function addSubscriberToGroups(
  email: string,
  groupIds: string[],
): Promise<{ ok: boolean; groups: string[] }> {
  return apiRequest<{ ok: boolean; groups: string[] }>(
    `/api/v1/admin/subscribers/${encodeURIComponent(email)}/groups`,
    { method: 'POST', body: { groupIds } },
  );
}

export async function removeSubscriberFromGroup(
  email: string,
  groupId: string,
): Promise<void> {
  return apiRequest<void>(
    `/api/v1/admin/subscribers/${encodeURIComponent(email)}/groups/${encodeURIComponent(groupId)}`,
    { method: 'DELETE' },
  );
}

/**
 * Returns the CSV-export URL with the admin's auth token in the
 * Authorization header, caller must fetch it themselves or use
 * a download form. For Phase 1 we expose a helper that triggers
 * a download in the browser via the existing apiRequest auth.
 */
// ─────────────────────────────────────────
// CSV import + bootstrap-from-past-sends (Phase 2, Pro)
// ─────────────────────────────────────────

export interface CsvImportResult {
  added: number;
  addedEmails: string[];
  updatedExisting: number;
  updatedExistingEmails: string[];
  blockedDeleted: number;
  blockedDeletedEmails: string[];
  invalid: number;
  invalidEmails: string[];
  createdGroups: Array<{ groupId: string; name: string }>;
}

export async function importSubscribersCsv(csv: string): Promise<CsvImportResult> {
  return apiRequest<CsvImportResult>('/api/v1/admin/subscribers/import-csv', {
    method: 'POST',
    body: { csv },
  });
}

export interface BootstrapCandidates {
  candidates: string[];
  count: number;
  totalUniqueInHistory: number;
  alreadyOnList: number;
}

export async function getBootstrapCandidates(): Promise<BootstrapCandidates> {
  return apiRequest<BootstrapCandidates>('/api/v1/admin/subscribers/bootstrap-candidates');
}

export async function bootstrapApply(
  emails: string[],
  assignToGroupId?: string,
): Promise<{ added: number; addedEmails: string[]; skipped: number; assignedToGroup: string | null }> {
  return apiRequest<{ added: number; addedEmails: string[]; skipped: number; assignedToGroup: string | null }>(
    '/api/v1/admin/subscribers/bootstrap-apply',
    { method: 'POST', body: { emails, assignToGroupId } },
  );
}

// ── Custom Domain (BYOD) ──────────────────────────────────────────
// Settings → Custom Domain card. Pro-tier + clubId allowlist gated.
// Lambda routes in lambda/routes/domains.ts; design doc:
// docs/features/custom-domains.md.

export type DomainStatus =
  | 'pending'
  | 'validating'
  | 'active'
  | 'failed'
  | 'decommissioning';

export interface ValidationCname {
  /** DNS record type. CNAME today; future-proofed for TXT if validation moves. */
  type?: 'CNAME' | 'TXT';
  name: string;
  value: string;
  /** What this record is FOR, drives the wizard's grouping + copy. */
  purpose?: 'ssl' | 'traffic';
}

export interface ApexInstructions {
  method: 'url-forwarding';
  source: string;
  target: string;
}

export interface CustomDomain {
  domain: string;
  clubId: string;
  status: DomainStatus;
  dnsPath: 'route53' | 'byo';
  addedAt: string;
  updatedAt: string;
  validationCnames: ValidationCname[];
  apexInstructions?: ApexInstructions | null;
  certId: string;
  notes: string;
  // Decommission window, populated when status === 'decommissioning'
  // (set by kickoffByodDecommission, cleared by reactivateByodDomains).
  decommissionAt?: string | null;
  decommissioningReason?: string | null;
}

export async function listDomains(): Promise<{ domains: CustomDomain[]; count: number }> {
  return apiRequest<{ domains: CustomDomain[]; count: number }>('/api/v1/admin/domains');
}

export async function addDomain(
  domain: string,
  dnsPath: 'route53' | 'byo' = 'byo',
): Promise<{ domain: CustomDomain }> {
  return apiRequest<{ domain: CustomDomain }>('/api/v1/admin/domains', {
    method: 'POST',
    body: { domain, dnsPath },
  });
}

export async function getDomainStatus(domain: string): Promise<{ domain: CustomDomain }> {
  return apiRequest<{ domain: CustomDomain }>(
    `/api/v1/admin/domains/${encodeURIComponent(domain)}`,
  );
}

export async function deleteDomain(domain: string): Promise<{ ok: boolean; domain: string }> {
  return apiRequest<{ ok: boolean; domain: string }>(
    `/api/v1/admin/domains/${encodeURIComponent(domain)}`,
    { method: 'DELETE' },
  );
}

// ─── BYOD billing (Custom Domain subscription) ─────────────────

export interface ByodBillingStatus {
  plan: 'free' | 'club' | 'pro';
  included: boolean;          // Pro → true, others → false
  monthlyPence: number;       // 0 for Pro, 300 for Club, 500 for Free
  setupPence: number;         // 0 except Free (1500)
  currency: 'gbp';
  subscriptionId: string | null;
  status: 'active' | 'past_due' | 'canceled' | 'incomplete' | 'unpaid' | null;
  currentPeriodEnd: string | null; // ISO date string
  canManageDomains: boolean;  // Pro OR active sub, the wizard's gate
}

export async function getByodBillingStatus(): Promise<ByodBillingStatus> {
  return apiRequest<ByodBillingStatus>('/api/v1/admin/byod/billing-status');
}

export async function createByodCheckout(): Promise<{ url: string }> {
  return apiRequest<{ url: string }>('/api/v1/admin/byod/checkout', { method: 'POST' });
}

export async function createByodPortal(): Promise<{ url: string }> {
  return apiRequest<{ url: string }>('/api/v1/admin/byod/portal', { method: 'POST' });
}

export async function exportSubscribersCsv(): Promise<Blob> {
  // apiRequest wraps fetch and adds auth. For binary/CSV we need to
  // call fetch directly to get the raw body. Reuse the same auth
  // header logic.
  const token = localStorage.getItem('authToken');
  const apiBase = (import.meta as any).env?.VITE_API_URL || 'https://api.allgrassroots.invalid';
  const response = await fetch(`${apiBase}/api/v1/admin/subscribers/export`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Export failed' }));
    throw new Error(err.error || 'Export failed');
  }
  return response.blob();
}

// ─── Preview (Club + Pro), drafts + Publish All / Discard ────────

export interface PreviewStatus {
  count: number;                              // total pending changes
  sections: { section: string; updatedAt: string }[];
  settingsDraft: boolean;                     // true if club-level settings have a draft
}

export async function getPreviewStatus(): Promise<PreviewStatus> {
  return apiRequest<PreviewStatus>('/api/v1/admin/preview/status');
}

export async function publishAllPreview(): Promise<{ publishedCount: number; sections: string[]; settingsPublished: boolean }> {
  return apiRequest('/api/v1/admin/preview/publish', { method: 'POST' });
}

export async function discardAllPreview(): Promise<{ discardedCount: number; sections: string[]; settingsCleared: boolean }> {
  return apiRequest('/api/v1/admin/preview/discard', { method: 'POST' });
}

/** 7-day token the preview site needs to read this club's drafts. */
export async function getPreviewToken(): Promise<{ token: string; expiresAt: string }> {
  return apiRequest('/api/v1/admin/preview/token', { method: 'POST' });
}

// ────────────────────────────────────────
// Match reports, who played, and what they did.
// Pilot: gated server-side on features.matchReports. See
// lambda/routes/match-reports.ts for whose numbers win (teams.statsMode).
// ────────────────────────────────────────

export type LineupRole = 'started' | 'sub' | 'unused';

/** Counts are flat keys named by sportConfig.stats.reportCounts. */
export type LineupEntry = { playerId: string; role: LineupRole } & Record<string, number | string>;

export interface MatchReportSquadMember {
  playerId: string;
  firstName: string;
  lastName?: string;
  position?: string;
  jerseyNumber?: number | null;
  /** Whether a Player of the Match award could name them on the website. */
  isPublic?: boolean;
}

/** The last match this team reported on, reduced to roles. Goals and assists
 *  are deliberately absent, they belong to one match. */
export interface PreviousLineup {
  fixtureId: string;
  date: string;
  opponent: string;
  homeAway: string;
  roles: Record<string, LineupRole>;
}

export interface MatchReport {
  fixtureId: string;
  teamId: string;
  previous: PreviousLineup | null;
  date: string;
  opponent: string;
  homeAway: string;
  status: string;
  goalsFor: number | null;
  goalsAgainst: number | null;
  lineup: LineupEntry[];
  reportedAt: string | null;
  /** Cricket: the innings and result, shown in the report rather than asked for. */
  innings?: Innings[];
  result?: InningsResult | null;
  potmPlayerIds?: string[];
  photo?: { url: string; caption?: string } | null;
  /** The whole squad travels with the report so the editor can render every
   *  player, including anyone who has no row in this match yet. */
  squad: MatchReportSquadMember[];
}

export async function getMatchReport(fixtureId: string): Promise<MatchReport> {
  return apiRequest<MatchReport>(`/api/v1/admin/fixtures/${fixtureId}/report`);
}

export async function saveMatchReport(
  fixtureId: string,
  lineup: LineupEntry[],
  score?: { goalsFor: number; goalsAgainst: number },
  extra: {
    potmPlayerIds?: string[];
    /** Absent leaves the stored photo alone; null removes it. */
    photo?: { url: string; caption: string; consent: boolean } | null;
  } = {},
): Promise<{ lineup: LineupEntry[]; season: string; statsMode: string; playersUpdated: number }> {
  return apiRequest(`/api/v1/admin/fixtures/${fixtureId}/report`, {
    method: 'PUT',
    body: { lineup, ...(score || {}), ...extra },
  });
}
